// AGC: TasksList - All tasks with filtering, sorting, and calendar view
// AGC: Visual Refresh Design System - Clean, data-centric layout
// AGC: All tasks are loaded from Firebase - filtered by user account
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { 
  Filter, 
  SortAsc, 
  Calendar, 
  AlertTriangle, 
  ChevronDown, 
  MapPin,
  List,
  CalendarDays,
  User,
  Tractor,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  ListTodo,
  Clock,
  Plus,
  Trash2,
  // AGC: Task type icons - minimalist single-color
  Sprout,
  Droplets,
  SprayCan,
  FlaskConical,
  Wheat,
  Wrench,
  Eye,
  ClipboardList,
  RefreshCw,
  Trophy,
  CheckCheck,
} from 'lucide-react'
import { Card, Badge, FloatingActionButton, EmptyState, ProgressBar } from '../shared'
import { formatDate, formatRelativeTime } from '@droidfarm/shared'
import { useQueryClient } from '@tanstack/react-query'
import { deleteAllTasks, deleteTask, finishTask, useTasksQuery, useInvalidateTasks } from '../../services/tasks'
import { getAssignableUsers } from '../../services/assigneesService'
import { useAuth } from '../../hooks/useAuth'
import { useLotes, useDeleteConfirm } from '../../context'
import { useTranslation } from '../../hooks/useTranslation'
import { usePullToRefresh } from '../../hooks/usePullToRefresh'
import { useHasPermission } from '../../hooks/useRBAC'
import { FARM_MACHINES } from '../../types/machine'
import type { Screen, Task, TaskFilter, TaskSort, TaskPriority, TaskCategory } from '../../types'
import { SorteioQuestCard } from '../sorteio/SorteioQuestCard'
import SorteioCompletionModal from '../sorteio/SorteioCompletionModal'

// AGC: Task category PNG icon mapping
const taskIconImgMap: Record<string, string> = {
  plantio: '/Machinery_icons/task/Plantio.png',
  irrigacao: '/Machinery_icons/task/irrigacao.png',
  pulverizacao: '/Machinery_icons/task/pulverizacao.png',
  adubacao: '/Machinery_icons/task/adbubacao.png',
  colheita: '/Machinery_icons/task/colheita.png',
  manutencao: '/Machinery_icons/task/Manuntencao.png',
  monitoramento: '/Machinery_icons/task/scout.png',
  outros: '/Machinery_icons/task/outros.png',
}

// AGC: Fallback Lucide icons for custom/unknown categories
const taskIconMap: Record<TaskCategory | 'default', React.ComponentType<{ className?: string }>> = {
  plantio: Sprout,
  irrigacao: Droplets,
  pulverizacao: SprayCan,
  adubacao: FlaskConical,
  colheita: Wheat,
  manutencao: Wrench,
  monitoramento: Eye,
  outros: ClipboardList,
  sorteio: Trophy,
  default: ListTodo,
}

// AGC: Task category colors - for icon background
const taskCategoryColors: Record<TaskCategory | 'default', string> = {
  plantio: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
  irrigacao: 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
  pulverizacao: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  adubacao: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  colheita: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  manutencao: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  monitoramento: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  outros: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  sorteio: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
  default: 'bg-navy-100 text-navy-600 dark:bg-navy-900/30 dark:text-navy-400',
}

// AGC: Detect Firebase UID (alphanumeric 19–30 chars, no spaces) - never show in UI
const looksLikeUid = (s: string): boolean => /^[A-Za-z0-9]{19,30}$/.test(s?.trim() || '')

// AGC: Resolve assignee to display name (never show raw ID in UI)
const getAssigneeDisplayName = (
  task: Task,
  assigneeMap: Map<string, string>,
  unassignedLabel: string,
  unknownUserLabel = '?'
): string => {
  const raw = task.assignedTo || ''
  const uid = task.assignedToId || (looksLikeUid(raw) ? raw : null)
  if (uid && assigneeMap.has(uid)) return assigneeMap.get(uid)!
  if (raw && !looksLikeUid(raw)) return raw
  if (uid) return unknownUserLabel
  return unassignedLabel
}

// AGC: Get task icon component, colors, and optional PNG path
const getTaskIcon = (category: TaskCategory | undefined): {
  Icon: React.ComponentType<{ className?: string }>
  bgClass: string
  textClass: string
  iconImg?: string
} => {
  const cat = category || 'default'
  const fullClass = taskCategoryColors[cat] || taskCategoryColors.default

  const textClass = fullClass.split(' ').filter(c => c.startsWith('text-') || c.includes(':text-')).join(' ')

  return {
    Icon: taskIconMap[cat] || taskIconMap.default,
    bgClass: fullClass,
    textClass: textClass || 'text-gray-600 dark:text-gray-400',
    iconImg: taskIconImgMap[cat],
  }
}

// AGC: Swipe row — left = delete, right = complete
const SWIPE_ACTION_WIDTH = 80
const SWIPE_THRESHOLD = 40

interface SwipeableTaskRowProps {
  task: Task
  onNavigateToDetail: () => void
  onDelete: (taskId: string) => Promise<void>
  onComplete?: (taskId: string) => Promise<void>
  children: React.ReactNode
}

const SwipeableTaskRow: React.FC<SwipeableTaskRowProps> = ({ task, onNavigateToDetail, onDelete, onComplete, children }) => {
  const { t } = useTranslation()
  const [offset, setOffset] = useState(0)
  const [isActing, setIsActing] = useState(false)
  const touchStartX = React.useRef(0)
  const touchStartOffset = React.useRef(0)

  const isInteractiveTarget = (target: EventTarget) =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || target instanceof HTMLButtonElement

  // AGC: Allow swipe both directions — negative = left (delete), positive = right (complete)
  const canComplete = onComplete && task.status !== 'completed'

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isInteractiveTarget(e.target)) return
    touchStartX.current = e.touches[0].clientX
    touchStartOffset.current = offset
  }
  const handleTouchMove = (e: React.TouchEvent) => {
    if (isInteractiveTarget(e.target)) return
    const dx = e.touches[0].clientX - touchStartX.current
    const maxRight = canComplete ? SWIPE_ACTION_WIDTH : 0
    const next = Math.max(-SWIPE_ACTION_WIDTH, Math.min(maxRight, touchStartOffset.current + dx))
    setOffset(next)
  }
  const handleTouchEnd = () => {
    if (offset < -SWIPE_THRESHOLD) setOffset(-SWIPE_ACTION_WIDTH)
    else if (offset > SWIPE_THRESHOLD && canComplete) setOffset(SWIPE_ACTION_WIDTH)
    else setOffset(0)
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isInteractiveTarget(e.target)) return
    touchStartX.current = e.clientX
    touchStartOffset.current = offset
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  const handlePointerMove = (e: React.PointerEvent) => {
    if (isInteractiveTarget(e.target)) return
    const dx = e.clientX - touchStartX.current
    const maxRight = canComplete ? SWIPE_ACTION_WIDTH : 0
    const next = Math.max(-SWIPE_ACTION_WIDTH, Math.min(maxRight, touchStartOffset.current + dx))
    setOffset(next)
  }
  const handlePointerUp = () => {
    if (offset < -SWIPE_THRESHOLD) setOffset(-SWIPE_ACTION_WIDTH)
    else if (offset > SWIPE_THRESHOLD && canComplete) setOffset(SWIPE_ACTION_WIDTH)
    else setOffset(0)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isActing) return
    setIsActing(true)
    try {
      await onDelete(task.id)
      setOffset(0)
    } finally {
      setIsActing(false)
    }
  }

  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isActing || !onComplete) return
    setIsActing(true)
    try {
      await onComplete(task.id)
      setOffset(0)
    } finally {
      setIsActing(false)
    }
  }

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* AGC: Green complete action strip (revealed when swiping right) */}
      {canComplete && (
        <div
          className="absolute left-0 top-0 bottom-0 flex items-center justify-center bg-emerald-500 text-white"
          style={{ width: SWIPE_ACTION_WIDTH }}
        >
          <button
            type="button"
            onClick={handleComplete}
            disabled={isActing}
            className="flex flex-col items-center justify-center gap-0.5 w-full h-full active:bg-emerald-600 disabled:opacity-70"
          >
            <CheckCheck className="w-5 h-5" />
            <span className="text-xs font-medium">{t('common.done')}</span>
          </button>
        </div>
      )}
      {/* AGC: Red delete action strip (revealed when swiping left) */}
      <div
        className="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-red-500 text-white"
        style={{ width: SWIPE_ACTION_WIDTH }}
      >
        <button
          type="button"
          onClick={handleDelete}
          disabled={isActing}
          className="flex flex-col items-center justify-center gap-0.5 w-full h-full active:bg-red-600 disabled:opacity-70"
          aria-label={t('tasks.deleteTask')}
        >
          <Trash2 className="w-5 h-5" />
          <span className="text-xs font-medium">{t('tasks.deleteTask')}</span>
        </button>
      </div>
      {/* AGC: Sliding content — swipe left for delete, right for complete */}
      <div
        className="relative transition-transform duration-150 ease-out touch-pan-y"
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onClick={(e) => {
          if (offset !== 0) {
            e.preventDefault()
            e.stopPropagation()
            setOffset(0)
          }
        }}
      >
        {children}
      </div>
    </div>
  )
}

interface TasksListProps {
  onNavigate: (screen: Screen, data?: { taskId?: string }) => void
  showOnlyOwnTasks?: boolean
  userId?: string
}

type ViewMode = 'list' | 'calendar'

/**
 * AGC: TasksList - Shows all tasks with filtering, sorting, and calendar view
 * AGC: Visual Refresh design:
 *   - Clean card-based layout
 *   - Subtle shadows and borders
 *   - Monochrome Lucide icons
 *   - Badge-based status indicators
 */
export const TasksList: React.FC<TasksListProps> = ({ 
  onNavigate, 
  showOnlyOwnTasks = false,
  userId 
}) => {
  const { t } = useTranslation()
  const { getAuthToken, user } = useAuth()
  const { lotes } = useLotes()
  const { confirmBeforeDelete, requestConfirmDelete } = useDeleteConfirm()
  
  // AGC: TanStack Query + localStorage disk cache — instant display, background refetch when stale
  const { data: tasks = [], isLoading } = useTasksQuery(!!user?.uid)
  const invalidateTasks = useInvalidateTasks()
  const queryClient = useQueryClient()

  const canCreateTask = useHasPermission('canCreateTask')

  // AGC: Pull-to-refresh
  const { scrollRef, pullDistance, isRefreshing, pullThreshold, touchHandlers } = usePullToRefresh(
    async () => { await invalidateTasks() }
  )
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [filter, setFilter] = useState<TaskFilter>('all') // AGC: Default to all tasks since filter tabs removed
  const [sort, setSort] = useState<TaskSort>('createdAt')
  // AGC: Top-level tab toggle — Pendente shows non-completed, Feitas shows completed
  const [activeTab, setActiveTab] = useState<'pendente' | 'feitas'>('pendente')
  
  const [selectedLot, setSelectedLot] = useState<string>('all')
  const [selectedPriority, setSelectedPriority] = useState<string>('all')
  const [selectedPerson, setSelectedPerson] = useState<string>('all')
  const [selectedMachine, setSelectedMachine] = useState<string>('all')
  
  const [showSortMenu, setShowSortMenu] = useState(false)
  const sortButtonRef = useRef<HTMLButtonElement>(null)
  const [sortMenuPosition, setSortMenuPosition] = useState<{ top: number; right: number } | null>(null)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false)
  const [isClearingAll, setIsClearingAll] = useState(false)
  // AGC: Map uid -> displayName so we never show raw IDs in the UI
  const [assigneeMap, setAssigneeMap] = useState<Map<string, string>>(new Map())
  // AGC: Sorteio completion modal — holds the sorteioId when user clicks "Receber meu número"
  const [showSorteioCompletion, setShowSorteioCompletion] = useState<string | null>(null)
  // AGC: Track exiting tasks for slide-out animation when completing on Pendente tab
  const prevPendenteIdsRef = useRef<Set<string>>(new Set())
  const [exitingTaskIds, setExitingTaskIds] = useState<Set<string>>(new Set())

  // AGC: Load assignable users to resolve assignedToId to display names (never show IDs in UI)
  useEffect(() => {
    let cancelled = false
    getAssignableUsers(user).then((assignees) => {
      if (cancelled) return
      const map = new Map<string, string>()
      assignees.forEach((a) => map.set(a.uid, a.displayName))
      setAssigneeMap(map)
    })
    return () => { cancelled = true }
  }, [user?.uid])

  // AGC: Refetch when a task is created or updated (e.g. via voice, TaskDetail save, swipe-complete)
  useEffect(() => {
    const refresh = () => { invalidateTasks() }
    window.addEventListener('task-created', refresh)
    window.addEventListener('task-updated', refresh)
    return () => {
      window.removeEventListener('task-created', refresh)
      window.removeEventListener('task-updated', refresh)
    }
  }, [invalidateTasks])

  // AGC: Detect tasks that became completed while on Pendente tab → animate out
  useEffect(() => {
    const currentPendenteIds = new Set(tasks.filter(t => t.status !== 'completed').map(t => t.id))
    if (activeTab !== 'pendente') {
      prevPendenteIdsRef.current = currentPendenteIds
      return
    }
    const newlyExiting = new Set<string>()
    prevPendenteIdsRef.current.forEach(id => {
      if (!currentPendenteIds.has(id)) newlyExiting.add(id)
    })
    if (newlyExiting.size > 0) {
      setExitingTaskIds(prev => new Set([...prev, ...newlyExiting]))
      const timer = setTimeout(() => {
        setExitingTaskIds(prev => {
          const next = new Set(prev)
          newlyExiting.forEach(id => next.delete(id))
          return next
        })
      }, 300)
      prevPendenteIdsRef.current = currentPendenteIds
      return () => clearTimeout(timer)
    }
    prevPendenteIdsRef.current = currentPendenteIds
  }, [tasks, activeTab])

  // AGC: Get unique values for filters
  const uniqueLots = useMemo(() => {
    const lotsMap = new Map<string, { id: string; name: string; count: number }>()
    tasks.forEach(task => {
      if (task.plotId && task.plotName) {
        const existing = lotsMap.get(task.plotId)
        if (existing) existing.count++
        else lotsMap.set(task.plotId, { id: task.plotId, name: task.plotName, count: 1 })
      }
    })
    return Array.from(lotsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [tasks])

  const uniquePersons = useMemo(() => {
    const personsMap = new Map<string, { name: string; count: number }>()
    const unassigned = t('tasks.unassigned')
    const unknownUser = '?'
    tasks.forEach(task => {
      const displayName = getAssigneeDisplayName(task, assigneeMap, unassigned, unknownUser)
      const key = task.assignedToId || task.assignedTo || displayName
      if (!key || displayName === unassigned) return
      if (displayName === unknownUser) return
      const existing = personsMap.get(key)
      if (existing) existing.count++
      else personsMap.set(key, { name: displayName, count: 1 })
    })
    return Array.from(personsMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [tasks, assigneeMap, t])

  const uniqueMachines = useMemo(() => {
    const machinesMap = new Map<string, { id: string; name: string; count: number }>()
    tasks.forEach(task => {
      const machineId = (task as any).machineId
      const machineName = (task as any).machineName
      if (machineId && machineName) {
        const existing = machinesMap.get(machineId)
        if (existing) existing.count++
        else machinesMap.set(machineId, { id: machineId, name: machineName, count: 1 })
      }
    })
    return Array.from(machinesMap.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [tasks])

  // AGC: Filter and sort tasks
  // AGC: When showOnlyOwnTasks (operador), only show tasks assigned to this user
  const filteredTasks = useMemo(() => {
    let filtered = tasks

    // AGC: Apply top-level tab filter first (Pendente = non-completed, Feitas = completed only)
    if (activeTab === 'pendente') {
      filtered = filtered.filter(t => t.status !== 'completed' || exitingTaskIds.has(t.id))
    } else {
      filtered = filtered.filter(t => t.status === 'completed')
    }

    if (showOnlyOwnTasks && userId) {
      filtered = filtered.filter(t => t.assignedToId === userId || t.createdBy === userId)
    }

    if (selectedLot !== 'all') {
      filtered = filtered.filter(t => t.plotId === selectedLot || (selectedLot.startsWith('field-') && `field-${t.fieldId}` === selectedLot))
    }
    if (selectedPerson !== 'all') {
      const unassigned = t('tasks.unassigned')
      filtered = filtered.filter(t => getAssigneeDisplayName(t, assigneeMap, unassigned) === selectedPerson)
    }
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(t => t.priority === selectedPriority)
    }
    if (selectedMachine !== 'all') {
      filtered = filtered.filter(t => (t as any).machineId === selectedMachine)
    }
    
    if (filter === 'pending') filtered = filtered.filter(t => t.status === 'pending')
    else if (filter === 'in-progress') filtered = filtered.filter(t => t.status === 'in-progress')
    else if (filter === 'completed') filtered = filtered.filter(t => t.status === 'completed')
    else if (filter === 'delayed') {
      filtered = filtered.filter(t => t.status !== 'completed' && t.status !== 'cancelled' && t.dueDate && new Date(t.dueDate) < new Date())
    }
    else if (filter === 'cancelled') filtered = filtered.filter(t => t.status === 'cancelled')
    
    if (sort === 'dueDate') {
      filtered = [...filtered].sort((a, b) => {
        // AGC: Tasks without dueDate sink to the bottom (treated as +Infinity)
        // AGC: Invalid date strings also go to the bottom
        const parseDue = (dateStr: string | undefined | null): number => {
          if (!dateStr) return Number.POSITIVE_INFINITY
          const t = new Date(dateStr).getTime()
          return isNaN(t) ? Number.POSITIVE_INFINITY : t
        }
        // AGC: ASCENDING order — earliest (most urgent) first. Previously descending, so users saw
        // AGC: least-urgent tasks first and missed critical overdue ones at the bottom.
        return parseDue(a.dueDate) - parseDue(b.dueDate)
      })
    } else if (sort === 'createdAt') {
      filtered = [...filtered].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
        return dateB - dateA
      })
    } else if (sort === 'priority') {
      const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 }
      filtered = [...filtered].sort((a, b) => (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0))
    } else if (sort === 'status') {
      const statusOrder: Record<string, number> = { 'in-progress': 3, 'pending': 2, 'completed': 1 }
      filtered = [...filtered].sort((a, b) => (statusOrder[b.status] || 0) - (statusOrder[a.status] || 0))
    }
    
    // AGC: Sort sorteio quest tasks to the top of the list so they're always visible
    filtered = [...filtered].sort((a, b) => {
      const aIsQuest = a.isSorteioQuest ? 1 : 0
      const bIsQuest = b.isSorteioQuest ? 1 : 0
      return bIsQuest - aIsQuest
    })

    return filtered
  }, [tasks, filter, sort, selectedLot, selectedPerson, selectedPriority, selectedMachine, showOnlyOwnTasks, userId, assigneeMap, t, activeTab, exitingTaskIds])

  const tasksByDate = useMemo(() => {
    const grouped = new Map<string, Task[]>()
    filteredTasks.forEach(task => {
      if (task.dueDate) {
        // AGC: Handle both string and Date object for dueDate
        const dateString = typeof task.dueDate === 'string' 
          ? task.dueDate 
          : task.dueDate instanceof Date 
            ? task.dueDate.toISOString() 
            : String(task.dueDate)
        const dateKey = dateString.split('T')[0]
        const existing = grouped.get(dateKey) || []
        grouped.set(dateKey, [...existing, task])
      }
    })
    return grouped
  }, [filteredTasks])

  // AGC: Calendar helpers
  const calendarMonth = calendarDate.getMonth()
  const calendarYear = calendarDate.getFullYear()
  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1).getDay()
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const prevMonth = () => setCalendarDate(new Date(calendarYear, calendarMonth - 1, 1))
  const nextMonth = () => setCalendarDate(new Date(calendarYear, calendarMonth + 1, 1))

  // AGC: Count active filters
  const activeFiltersCount = [selectedLot !== 'all', selectedPerson !== 'all', selectedPriority !== 'all', selectedMachine !== 'all'].filter(Boolean).length

  const clearFilters = () => {
    setSelectedLot('all')
    setSelectedPerson('all')
    setSelectedPriority('all')
    setSelectedMachine('all')
  }

  // AGC: Clear all tasks for current user (start from zero) - called after user confirms
  const handleConfirmClearAll = async () => {
    try {
      setIsClearingAll(true)
      const token = await getAuthToken()
      await deleteAllTasks(token)
      setShowClearAllConfirm(false)
      invalidateTasks()
    } catch (e) {
      console.error('AGC: Error clearing all tasks:', e)
    } finally {
      setIsClearingAll(false)
    }
  }

  // AGC: Delete single task (used by swipe-to-delete row)
  const handleDeleteTask = async (taskId: string) => {
    const token = await getAuthToken()
    await deleteTask(taskId, token)
    invalidateTasks()
  }

  // AGC: When Settings > Segurança > Sistema "confirm before delete" is ON, show modal first
  const onDeleteTask = (taskId: string): Promise<void> => {
    if (confirmBeforeDelete) {
      requestConfirmDelete({ onConfirm: () => handleDeleteTask(taskId) })
      return Promise.resolve()
    }
    return handleDeleteTask(taskId)
  }

  // AGC: Swipe-right to complete — calls finishTask endpoint, then auto-refreshes list + inventory
  const handleCompleteTask = async (taskId: string) => {
    const token = await getAuthToken()
    await finishTask(taskId, token)
    invalidateTasks()
    queryClient.invalidateQueries({ queryKey: ['inventory-items'] })
    window.dispatchEvent(new CustomEvent('task-updated'))
  }

  const sortOptions: { id: TaskSort; label: string }[] = [
    { id: 'dueDate', label: t('tasks.sortDueDate') },
    { id: 'createdAt', label: t('tasks.sortDateAdded') },
    { id: 'priority', label: t('tasks.sortPriority') },
    { id: 'status', label: t('tasks.sortStatus') },
  ]

  const priorityOptions: { value: TaskPriority | 'all'; label: string; variant: 'error' | 'warning' | 'success' | 'default' }[] = [
    { value: 'all', label: t('common.all'), variant: 'default' },
    { value: 'high', label: t('tasks.priorityHigh'), variant: 'error' },
    { value: 'medium', label: t('tasks.priorityMedium'), variant: 'warning' },
    { value: 'low', label: t('tasks.priorityLow'), variant: 'success' },
  ]

  return (
    <div
      ref={scrollRef}
      className="min-h-screen app-bg pb-20 transition-colors overflow-y-auto"
      {...touchHandlers}
    >
      {/* AGC: Pull-to-refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden transition-all duration-200 app-bg"
          style={{ height: isRefreshing ? pullThreshold : pullDistance }}
        >
          <RefreshCw className={`w-4 h-4 text-green-600 dark:text-green-400 transition-transform ${isRefreshing ? 'animate-spin' : ''} ${pullDistance >= pullThreshold ? 'scale-110' : 'scale-90 opacity-60'}`} />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {isRefreshing ? t('common.syncing') : pullDistance >= pullThreshold ? t('common.releaseToRefresh') : t('common.pullToRefresh')}
          </span>
        </div>
      )}

      {/* AGC: Confirm clear all tasks modal */}
      {showClearAllConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[90]" onClick={() => !isClearingAll && setShowClearAllConfirm(false)} role="button" aria-label="Close" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && !isClearingAll && setShowClearAllConfirm(false)} />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
            <Card className="max-w-sm w-full p-4 space-y-4" onClick={e => e.stopPropagation()}>
              <p className="text-gray-700 dark:text-gray-200">{t('tasks.clearAllConfirm')}</p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowClearAllConfirm(false)}
                  disabled={isClearingAll}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmClearAll}
                  disabled={isClearingAll}
                  className="px-3 py-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {isClearingAll ? (
                    <span className="inline-flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" /> ...</span>
                  ) : (
                    t('tasks.clearAll')
                  )}
                </button>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* AGC: Header - Solid green gradient */}
      <header 
        className="px-4 pt-4 pb-4 sticky top-0 z-10 rounded-b-2xl relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
      >
        {/* AGC: Decorative background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
          />
        </div>
        
        {/* AGC: Title row — icon + title LEFT, action buttons RIGHT */}
        <div className="flex items-center justify-between mb-3 relative z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <ListTodo className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-semibold text-white">
              {t('tabs.tasks')}
            </h1>
          </div>

          {/* AGC: Action buttons — same row as title, 25% bigger icons (w-5 h-5) */}
          <div className="flex items-center gap-2">
            <div className="flex bg-white/15 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2.5 rounded-md transition-all ${
                  viewMode === 'list'
                    ? 'bg-white/30 shadow-sm text-white'
                    : 'text-white/60 hover:text-white'
                }`}
                title={t('tasks.listView') || 'List view'}
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-2.5 rounded-md transition-all ${
                  viewMode === 'calendar'
                    ? 'bg-white/30 shadow-sm text-white'
                    : 'text-white/60 hover:text-white'
                }`}
                title={t('tasks.calendarView') || 'Calendar view'}
              >
                <CalendarDays className="w-5 h-5" />
              </button>
            </div>

            <button
              onClick={() => setShowFilterModal(true)}
              className={`relative p-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeFiltersCount > 0
                  ? 'bg-white text-[var(--color-accent)]'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
              title={t('common.filters')}
            >
              <Filter className="w-5 h-5" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {tasks.length > 0 && !showOnlyOwnTasks && (
              <button
                onClick={() => setShowClearAllConfirm(true)}
                className="p-2.5 rounded-lg bg-white/15 text-white/80 hover:bg-red-500/80 hover:text-white text-sm font-medium transition-colors"
                title={t('tasks.clearAll')}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}

            <div className="relative">
              <button
                ref={sortButtonRef}
                onClick={() => {
                  if (!showSortMenu && sortButtonRef.current) {
                    const rect = sortButtonRef.current.getBoundingClientRect()
                    setSortMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
                  } else if (!showSortMenu) setSortMenuPosition(null)
                  setShowSortMenu(!showSortMenu)
                }}
                className="p-2.5 rounded-lg bg-white/15 text-white hover:bg-white/25 text-sm font-medium transition-colors border-2 border-[#0B5F4A] ring-2 ring-white/70"
                title={sortOptions.find(s => s.id === sort)?.label}
              >
                <SortAsc className="w-5 h-5" />
              </button>
              
              {showSortMenu && typeof document !== 'undefined' && sortMenuPosition && createPortal(
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => { setShowSortMenu(false); setSortMenuPosition(null) }} aria-hidden="true" />
                  <Card
                    variant="elevated"
                    padding="none"
                    className="fixed w-36 overflow-hidden shadow-xl z-[9999] bg-white dark:bg-gray-800"
                    style={{ top: sortMenuPosition.top, right: sortMenuPosition.right, left: 'auto' }}
                  >
                    {sortOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => { setSort(option.id); setShowSortMenu(false); setSortMenuPosition(null) }}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          sort === option.id 
                            ? 'text-navy-600 dark:text-navy-400 font-medium bg-navy-50 dark:bg-navy-900/20' 
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </Card>
                </>,
                document.body
              )}
            </div>
          </div>
        </div>

        {/* AGC: Pendente / Feitas segmented toggle */}
        <div className="flex items-center mb-3 rounded-xl overflow-hidden relative z-10"
          style={{ background: 'rgba(255,255,255,0.10)' }}
        >
          <button
            onClick={() => setActiveTab('pendente')}
            className={`flex-1 py-2.5 text-sm font-bold text-center transition-all duration-200 ${
              activeTab === 'pendente' ? 'text-white' : 'text-white/60'
            }`}
            style={activeTab === 'pendente' ? {
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              boxShadow: '0 2px 8px rgba(5, 150, 105, 0.4), 0 1px 3px rgba(0,0,0,0.2)',
              transform: 'translateY(-1px)',
              borderRadius: '0.75rem',
            } : {
              background: 'rgba(248, 113, 113, 0.15)',
              transform: 'translateY(0)',
            }}
          >
            {t('tasks.tabPendente')}
          </button>
          <div className="w-px h-6 bg-white/20 flex-shrink-0" />
          <button
            onClick={() => setActiveTab('feitas')}
            className={`flex-1 py-2.5 text-sm font-bold text-center transition-all duration-200 ${
              activeTab === 'feitas' ? 'text-white' : 'text-white/60'
            }`}
            style={activeTab === 'feitas' ? {
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              boxShadow: '0 2px 8px rgba(5, 150, 105, 0.4), 0 1px 3px rgba(0,0,0,0.2)',
              transform: 'translateY(-1px)',
              borderRadius: '0.75rem',
            } : {
              background: 'rgba(248, 113, 113, 0.15)',
              transform: 'translateY(0)',
            }}
          >
            {t('tasks.tabFeitas')}
          </button>
        </div>

        {/* AGC: Active Filters - compact inline chips (only if filters active) */}
        {activeFiltersCount > 0 && (
          <div className="flex items-center gap-1.5 mt-3 overflow-x-auto hide-scrollbar relative z-10">
            {selectedLot !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/20 text-white text-xs">
                <MapPin className="w-3 h-3" />
                {uniqueLots.find(l => l.id === selectedLot)?.name.split(' ')[0]}
              </span>
            )}
            {selectedPerson !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/20 text-white text-xs">
                <User className="w-3 h-3" />
                {selectedPerson.split(' ')[0]}
              </span>
            )}
            {selectedPriority !== 'all' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white/20 text-white text-xs">
                {priorityOptions.find(p => p.value === selectedPriority)?.label}
              </span>
            )}
            {selectedMachine !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/20 text-white text-xs">
                <Tractor className="w-3 h-3" />
                {uniqueMachines.find(m => m.id === selectedMachine)?.name.split(' ')[0]}
              </span>
            )}
            <button onClick={clearFilters} className="text-xs text-white/70 hover:text-white flex-shrink-0 ml-auto">
              {t('common.clear')}
            </button>
          </div>
        )}
      </header>

      {/* AGC: Keyframes for task completion slide-out animation */}
      <style>{`
        @keyframes task-slide-out-left {
          0% { opacity: 1; transform: translateX(0); max-height: 200px; }
          70% { opacity: 0; transform: translateX(-100%); max-height: 200px; }
          100% { opacity: 0; transform: translateX(-100%); max-height: 0; margin: 0; padding: 0; }
        }
        .animate-task-exit { animation: task-slide-out-left 300ms ease-in forwards; overflow: hidden; }
      `}</style>

      {/* AGC: Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 z-[90] flex items-end sm:items-center justify-center">
          <Card variant="elevated" padding="none" className="w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-gray-50">{t('common.filters')}</h3>
              <button onClick={() => setShowFilterModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
              {/* AGC: Lot Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-navy-600" />
                  {t('fields.lot')}
                </label>
                <select
                  value={selectedLot}
                  onChange={(e) => setSelectedLot(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">{t('common.all')} ({tasks.length})</option>
                  {uniqueLots.map((lot) => (
                    <option key={lot.id} value={lot.id}>{lot.name} ({lot.count})</option>
                  ))}
                </select>
              </div>

              {/* AGC: Person Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4 text-purple-500" />
                  {t('tasks.assignedTo')}
                </label>
                <select
                  value={selectedPerson}
                  onChange={(e) => setSelectedPerson(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">{t('common.all')}</option>
                  {uniquePersons.map((person) => (
                    <option key={person.name} value={person.name}>{person.name} ({person.count})</option>
                  ))}
                </select>
              </div>

              {/* AGC: Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tasks.priority')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {priorityOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setSelectedPriority(option.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedPriority === option.value
                          ? 'bg-navy-700 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* AGC: Machine Filter */}
              {uniqueMachines.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Tractor className="w-4 h-4 text-amber-500" />
                    {t('tasks.machine')}
                  </label>
                  <select
                    value={selectedMachine}
                    onChange={(e) => setSelectedMachine(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  >
                    <option value="all">{t('common.all')}</option>
                    {uniqueMachines.map((machine) => (
                      <option key={machine.id} value={machine.id}>{machine.name} ({machine.count})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={clearFilters}
                className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium"
              >
                {t('common.clearFilters')}
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-600 font-medium"
              >
                {t('common.apply')}
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* AGC: Content */}
      {viewMode === 'list' ? (
        <div className="px-4 py-4 space-y-3 stagger-children">
          {/* AGC: Sort-of label - top right above first entry so user knows current arrangement */}
          <div className="flex justify-end">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('tasks.sortOf')}: {sortOptions.find(s => s.id === sort)?.label}
            </p>
          </div>
          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 text-navy-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <EmptyState
              icon={<ListTodo />}
              title={filter === 'all' && activeFiltersCount === 0 && activeTab === 'pendente'
                ? t('tasks.noTasks')
                : t('tasks.noTasksFound')}
              description={filter === 'all' && activeFiltersCount === 0 && activeTab === 'pendente'
                ? t('tasks.addFirstTask')
                : undefined}
              action={filter === 'all' && activeFiltersCount === 0 && activeTab === 'pendente' ? {
                label: t('tasks.newTask'),
                onClick: () => onNavigate('new-task'),
                icon: <Plus className="w-4 h-4" />,
              } : undefined}
              secondaryAction={activeFiltersCount > 0 ? {
                label: t('common.clearFilters'),
                onClick: clearFilters,
              } : undefined}
            />
          ) : (
            filteredTasks.map((task) => {
              // AGC: Apply slide-out animation for tasks exiting the Pendente tab
              const isExiting = exitingTaskIds.has(task.id)
              // AGC: Sorteio quest tasks render with their special card (gold border, checklist)
              if (task.isSorteioQuest) {
                return (
                  <div key={task.id} className={isExiting ? 'animate-task-exit' : ''}>
                    <SorteioQuestCard
                      task={task}
                      onOpenDetail={(taskId) => onNavigate('task-detail', { selectedTaskId: taskId })}
                      onQuestComplete={(sorteioId) => {
                        setShowSorteioCompletion(sorteioId)
                      }}
                    />
                  </div>
                )
              }
              return (
                <div key={task.id} className={isExiting ? 'animate-task-exit' : ''}>
                  <SwipeableTaskRow
                    task={task}
                    onNavigateToDetail={() => onNavigate('task-detail', { selectedTaskId: task.id })}
                    onDelete={onDeleteTask}
                    onComplete={handleCompleteTask}
                  >
                    <TaskCard
                      task={task}
                      onClick={() => onNavigate('task-detail', { selectedTaskId: task.id })}
                      assigneeMap={assigneeMap}
                    />
                  </SwipeableTaskRow>
                </div>
              )
            })
          )}
        </div>
      ) : (
        <div className="px-4 py-4">
          {/* AGC: Calendar Header */}
          <Card variant="default" padding="md" className="mb-4">
            <div className="flex items-center justify-between">
              <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <h3 className="font-semibold text-gray-900 dark:text-gray-50">
                {monthNames[calendarMonth]} {calendarYear}
              </h3>
              <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </Card>

          {/* AGC: Calendar Grid */}
          <Card variant="default" padding="sm">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">{day}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                <div key={`empty-${i}`} className="h-12" />
              ))}
              
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                const dayTasks = tasksByDate.get(dateStr) || []
                const isToday = new Date().toISOString().split('T')[0] === dateStr
                const hasOverdue = dayTasks.some(t => t.status !== 'completed' && new Date(t.dueDate) < new Date())
                
                return (
                  <div
                    key={day}
                    className={`h-12 rounded-lg p-1 cursor-pointer transition-colors ${
                      isToday 
                        ? 'bg-navy-100 dark:bg-navy-900/30 ring-1 ring-navy-500' 
                        : dayTasks.length > 0 
                          ? hasOverdue 
                            ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' 
                            : 'bg-navy-50 dark:bg-navy-900/20 hover:bg-navy-100 dark:hover:bg-navy-900/30'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => dayTasks.length > 0 && onNavigate('task-detail', { selectedTaskId: dayTasks[0].id })}
                  >
                    <div className={`text-xs ${isToday ? 'text-navy-600 dark:text-navy-400 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                      {day}
                    </div>
                    {dayTasks.length > 0 && (
                      <div className="flex gap-0.5 mt-0.5 flex-wrap">
                        {dayTasks.slice(0, 3).map((task, idx) => (
                          <div
                            key={idx}
                            className={`w-1.5 h-1.5 rounded-full ${
                              task.status === 'completed' ? 'bg-emerald-500' :
                              task.status === 'in-progress' ? 'bg-sky-500' :
                              hasOverdue ? 'bg-red-500' : 'bg-amber-500'
                            }`}
                          />
                        ))}
                        {dayTasks.length > 3 && (
                          <span className="text-[8px] text-gray-500">+{dayTasks.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>

          {/* AGC: Tasks for selected month */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tasks.monthTasks')} ({filteredTasks.filter(t => {
                if (!t.dueDate) return false
                const taskDate = new Date(t.dueDate)
                return taskDate.getMonth() === calendarMonth && taskDate.getFullYear() === calendarYear
              }).length})
            </h4>
            <div className="space-y-2">
              {filteredTasks.filter(t => {
                if (!t.dueDate) return false
                const taskDate = new Date(t.dueDate)
                return taskDate.getMonth() === calendarMonth && taskDate.getFullYear() === calendarYear
              }).slice(0, 5).map((task) => (
                <SwipeableTaskRow
                  key={task.id}
                  task={task}
                  onNavigateToDetail={() => onNavigate('task-detail', { selectedTaskId: task.id })}
                  onDelete={onDeleteTask}
                  onComplete={handleCompleteTask}
                >
                  <TaskCard task={task} onClick={() => onNavigate('task-detail', { selectedTaskId: task.id })} compact assigneeMap={assigneeMap} />
                </SwipeableTaskRow>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AGC: FAB only - BottomNav is rendered by MainMenu (no page should have own BottomNav) */}
      {!showOnlyOwnTasks && canCreateTask && (
        <FloatingActionButton onClick={() => onNavigate('new-task')} />
      )}

      {/* AGC: Sorteio completion modal — shown when user taps "Receber meu número" on a quest card */}
      {showSorteioCompletion && (
        <SorteioCompletionModal
          sorteioId={showSorteioCompletion}
          onComplete={(_luckyNumber) => {
            setShowSorteioCompletion(null)
            // AGC: Refresh task list so completed quest card reflects new state
            invalidateTasks()
          }}
          onClose={() => setShowSorteioCompletion(null)}
        />
      )}
    </div>
  )
}

/**
 * AGC: Task Card with Visual Refresh design
 */
interface TaskCardProps {
  task: Task
  onClick: () => void
  compact?: boolean
  assigneeMap?: Map<string, string>
}

const TaskCard: React.FC<TaskCardProps> = React.memo(({ task, onClick, compact = false, assigneeMap }) => {
  const { t } = useTranslation()
  const unassignedLabel = t('tasks.unassigned')
  
  if (!task || !task.id) return null

  // AGC: Safely parse dueDate - handle invalid dates
  const dueDateValue = task.dueDate ? new Date(task.dueDate) : null
  const isValidDate = dueDateValue && !isNaN(dueDateValue.getTime())
  
  const isOverdue = task.status !== 'completed' && isValidDate && dueDateValue < new Date()
  const assignedName = (assigneeMap ? getAssigneeDisplayName(task, assigneeMap, unassignedLabel) : task.assignedTo) || unassignedLabel
  const machineInfo = FARM_MACHINES.find(m => m.id === (task as any).machineId)
  // AGC: Prefer stored machineName (voice/user machines) when machineInfo is null
  const displayMachineName = (task as any).machineName || machineInfo?.name
  const { Icon: TaskIcon, bgClass: taskBgClass, textClass: taskTextClass, iconImg: taskIconImg } = getTaskIcon(task.category)
  
  // AGC: Safe date formatter
  const formatTaskDate = (date: Date | string | null | undefined): string => {
    if (!date) return t('tasks.noDate') || 'No date'
    try {
      const d = typeof date === 'string' ? new Date(date) : date
      if (isNaN(d.getTime())) return t('tasks.noDate') || 'No date'
      return formatDate(d)
    } catch {
      return t('tasks.noDate') || 'No date'
    }
  }
  
  // AGC: Translate status and priority labels
  const statusLabels: Record<string, string> = {
    'pending': t('tasks.statusPending'),
    'in-progress': t('tasks.statusInProgress'),
    'completed': t('tasks.statusCompleted'),
    'cancelled': t('tasks.statusCancelled'),
  }
  
  const priorityLabels: Record<string, string> = {
    'high': t('tasks.priorityHigh'),
    'medium': t('tasks.priorityMedium'),
    'low': t('tasks.priorityLow'),
  }

  const statusVariant: Record<string, 'default' | 'accent' | 'success' | 'warning' | 'error' | 'info'> = {
    'pending': 'warning',
    'in-progress': 'info',
    'completed': 'success',
    'cancelled': 'default',
  }

  const priorityVariant: Record<string, 'default' | 'accent' | 'success' | 'warning' | 'error' | 'info'> = {
    'high': 'error',
    'medium': 'warning',
    'low': 'success',
  }

  if (compact) {
    return (
      <button
        onClick={onClick}
        className="w-full text-left p-3 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-200 hover:bg-slate-200/70 dark:hover:bg-slate-700/70 active:scale-[0.98]"
      >
        <div className="flex items-center gap-3">
          {/* AGC: Task type icon - compact version */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${taskBgClass}`}>
            {taskIconImg ? <img src={taskIconImg} alt="" className="w-5 h-5 object-contain" /> : <TaskIcon className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{task.title || 'Untitled'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {formatTaskDate(task.dueDate)}
            </p>
          </div>
          {isOverdue && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
        </div>
      </button>
    )
  }

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-200 hover:bg-slate-200/70 dark:hover:bg-slate-700/70 hover:border-slate-300 dark:hover:border-slate-600 active:scale-[0.98]"
    >
      {/* AGC: Top row - Avatar + Title with inline icon + Badges */}
      <div className="flex items-start gap-3">
        {/* AGC: Assigned person avatar - small rounded */}
        <div className="flex-shrink-0">
          {task.assignedToAvatar ? (
            <img src={task.assignedToAvatar} alt={assignedName} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-300/50 dark:bg-gray-600 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{assignedName.charAt(0)}</span>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* AGC: Title with small inline task icon */}
          <div className="flex items-center gap-1.5">
            {taskIconImg ? <img src={taskIconImg} alt="" className="w-4 h-4 object-contain flex-shrink-0" /> : <TaskIcon className={`w-4 h-4 flex-shrink-0 ${taskTextClass}`} />}
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{task.title || 'Untitled'}</h3>
            {isOverdue && <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
            {task.fieldName || 'No field'} {task.plotName && `• ${task.plotName}`}
          </p>
        </div>
        
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Badge variant={isOverdue ? 'error' : statusVariant[task.status] || 'default'} size="sm">
            {isOverdue ? t('tasks.overdue') : statusLabels[task.status] || task.status}
          </Badge>
          <Badge variant={priorityVariant[task.priority] || 'default'} size="sm">
            {priorityLabels[task.priority] || task.priority}
          </Badge>
        </div>
      </div>

      {/* AGC: Bottom row - Date + Machine + Assignee name */}
      <div className="flex items-center gap-4 mt-3 pl-13">
        <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <Calendar className="w-3.5 h-3.5" />
          <span className={isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
            {formatTaskDate(task.dueDate)}
          </span>
        </div>
        
        {displayMachineName && (
          <Badge variant="warning" size="sm" icon={<Tractor className="w-3 h-3" />}>
            {displayMachineName.split(' ')[0]}
          </Badge>
        )}
        
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">{assignedName.split(' ')[0]}</span>
      </div>

      {task.status !== 'pending' && task.status !== 'completed' && (
        <div className="mt-3">
          <ProgressBar progress={task.progress || 0} size="sm" showLabel />
        </div>
      )}
    </button>
  )
})

TaskCard.displayName = 'TaskCard'

export default TasksList
